import type { ModuleManifest } from "@playbookai/ui";
import {
  lamaAssessmentHref,
  PLATFORM_ENTITY_TYPES,
  PLATFORM_MODULE_IDS,
  type PlatformEntityRef,
  type PlatformModuleId,
  type PlatformNavigationAction,
} from "@playbookai/types";
import { useAuthStore } from "../auth/auth-store.js";
import { isModuleAvailable } from "../modules/index.js";

export interface PlatformNavigationDestination {
  readonly module: PlatformModuleId;
  readonly entityId: string;
  readonly route: string;
  readonly tabId: string;
  readonly label: string;
  readonly shellManifest?: ModuleManifest | undefined;
  readonly instanceId?: string | undefined;
  /**
   * False for a route outside a module's tab space: the module-neutral
   * `/app/tti` (no tab at all) and KPI Manager's `/settings/kpi` (the shell's
   * Settings tab owns it, so a tab of its own would be a second pill).
   */
  readonly opensTab: boolean;
}

interface NavigationManifest {
  readonly shellModuleId?: string | undefined;
  readonly requiresInstance: boolean;
  readonly routeFor: (
    ref: PlatformEntityRef,
    instanceId: string | undefined,
  ) => string | null;
}

/**
 * Shell-owned semantic route registry. Model output never supplies a path:
 * it supplies a typed entity ref, and only a matching row here can produce a
 * canonical same-origin destination.
 */
const PLAYER_AI_NAVIGATION_MANIFESTS: Readonly<
  Record<PlatformModuleId, NavigationManifest>
> = {
  solve: {
    shellModuleId: "solve",
    requiresInstance: true,
    routeFor: (ref, instanceId) =>
      ref.entity_type === "solve" && instanceId
        ? `/app/solve/${segment(instanceId)}/${segment(ref.entity_id)}`
        : null,
  },
  vsm: {
    shellModuleId: "vsm",
    requiresInstance: true,
    routeFor: (ref, instanceId) =>
      ref.entity_type === "vsm" && instanceId
        ? `/app/vsm/${segment(instanceId)}/${segment(ref.entity_id)}`
        : null,
  },
  hoshin: {
    shellModuleId: "hoshin",
    requiresInstance: false,
    routeFor: (ref) => {
      if (ref.entity_type === "hoshin_plan") {
        return `/app/hoshin/${segment(ref.entity_id)}`;
      }
      // A target's own page (its id IS the TTI id), not the whole plan.
      if (ref.entity_type === "target") {
        return `/app/tti/${segment(ref.entity_id)}`;
      }
      return null;
    },
  },
  lama: {
    shellModuleId: "lama",
    requiresInstance: true,
    routeFor: (ref, instanceId) => {
      if (
        ref.entity_type === "assignment" &&
        ref.parent?.entity_type === "wave"
      ) {
        return lamaAssessmentHref(
          ref.parent.entity_id,
          undefined,
          ref.entity_id,
        );
      }
      if (
        ref.entity_type === "wave" &&
        ref.parent?.entity_type !== "campaign"
      ) {
        return lamaAssessmentHref(ref.entity_id);
      }
      if (!instanceId) return null;
      if (ref.entity_type === "questionnaire") {
        return `/app/lama/${segment(instanceId)}/questionnaires/${segment(ref.entity_id)}`;
      }
      if (ref.entity_type === "campaign") {
        return `/app/lama/${segment(instanceId)}/campaigns/${segment(ref.entity_id)}`;
      }
      if (ref.entity_type === "report") {
        // Campaign-derived: the report ref's entity_id IS the campaign id, and
        // the report route is keyed by it (ADR-0054 — no report table).
        return `/app/lama/${segment(instanceId)}/reports/${segment(ref.entity_id)}`;
      }
      // A `question` or `assignment` ref is not independently navigable; the
      // resolver walks up its parent chain, and the wave parent lands on the
      // assessment route above with the question as a selection hint only.
      return null;
    },
  },
  kpi: {
    requiresInstance: false,
    routeFor: (ref) =>
      ref.entity_type === "kpi"
        ? `/settings/kpi/${segment(ref.entity_id)}`
        : null,
  },
};

const PLATFORM_MODULE_SET: ReadonlySet<string> = new Set(PLATFORM_MODULE_IDS);

/** Reject persisted/model-shaped values before the shell route registry sees them. */
export function isPlatformNavigationAction(
  value: unknown,
): value is PlatformNavigationAction {
  if (typeof value !== "object" || value === null) return false;
  const action = value as Record<string, unknown>;
  return (
    action["action_id"] === "platform.navigate@1" &&
    action["kind"] === "platform.navigate@1" &&
    action["contract_version"] === 1 &&
    typeof action["label"] === "string" &&
    action["label"].trim().length > 0 &&
    action["label"].length <= 120 &&
    isPlatformEntityRef(action["target"])
  );
}

/**
 * May this workspace use the module, by the one rule (ADR-0100)? Read from the
 * session at call time, so every caller gets KPI Manager — which has no shell
 * module for `enabledModules` to narrow — gated the same way.
 */
function availableInSession(module: PlatformModuleId): boolean {
  return isModuleAvailable(
    module,
    useAuthStore.getState().claims?.enabledModules,
  );
}

export function resolvePlatformNavigation(
  action: PlatformNavigationAction,
  enabledModules: readonly ModuleManifest[],
  instanceIdFor: (module: PlatformModuleId) => string,
  // ADR-0100: an answer never opens an off module, KPI Manager included.
  isAvailable: (module: PlatformModuleId) => boolean = availableInSession,
): PlatformNavigationDestination | null {
  // A child result navigates through an explicit parent chain. If no parent
  // has a registered route, the action is rejected instead of guessing.
  let ref: PlatformEntityRef | undefined = action.target;
  const instances = new Map<PlatformModuleId, string>();
  for (let depth = 0; ref && depth < 5; depth += 1) {
    const manifest = PLAYER_AI_NAVIGATION_MANIFESTS[ref.module];
    const shellManifest = manifest.shellModuleId
      ? enabledModules.find(
          (candidate) => candidate.id === manifest.shellModuleId,
        )
      : undefined;
    if ((!manifest.shellModuleId || shellManifest) && isAvailable(ref.module)) {
      let instanceId: string | undefined;
      if (manifest.requiresInstance) {
        instanceId = instances.get(ref.module);
        if (!instanceId) {
          instanceId = instanceIdFor(ref.module);
          instances.set(ref.module, instanceId);
        }
      }
      const route = manifest.routeFor(ref, instanceId);
      if (route) {
        const questionId =
          action.target.module === "lama" &&
          action.target.entity_type === "question" &&
          ref.entity_type === "assignment"
            ? action.target.entity_id
            : undefined;
        return {
          module: ref.module,
          entityId: ref.entity_id,
          route: questionId
            ? `${route}&questionId=${segment(questionId)}`
            : route,
          tabId: `${ref.module}-${ref.entity_id}`,
          label: action.label,
          // Only a module's own routes get a tab. The shell mints none for
          // /app/tti (App.tsx), and a module with no shell manifest — KPI
          // Manager — lives in the Settings tab, which the shell opens itself.
          opensTab:
            shellManifest !== undefined && !route.startsWith("/app/tti/"),
          ...(shellManifest ? { shellManifest } : {}),
          ...(instanceId ? { instanceId } : {}),
        };
      }
    }
    ref = ref.parent;
  }
  return null;
}

function segment(value: string): string {
  return encodeURIComponent(value);
}

function isPlatformEntityRef(
  value: unknown,
  depth = 0,
): value is PlatformEntityRef {
  if (depth > 4 || typeof value !== "object" || value === null) return false;
  const ref = value as Record<string, unknown>;
  const module = ref["module"];
  const entityType = ref["entity_type"];
  const entityId = ref["entity_id"];
  if (
    typeof module !== "string" ||
    !PLATFORM_MODULE_SET.has(module) ||
    typeof entityType !== "string" ||
    !(
      PLATFORM_ENTITY_TYPES[module as PlatformModuleId] as readonly string[]
    ).includes(entityType) ||
    typeof entityId !== "string" ||
    entityId.length === 0 ||
    entityId.length > 200 ||
    (ref["subsection"] !== undefined &&
      (typeof ref["subsection"] !== "string" || ref["subsection"].length > 120))
  ) {
    return false;
  }
  return (
    ref["parent"] === undefined || isPlatformEntityRef(ref["parent"], depth + 1)
  );
}
